import { Component, ElementRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BugReportKind, MAX_DESCRIPTION } from '../../bug-report.model';
import { BugReportService } from '../../services/bug-report.service';
import {
  Annotation,
  CaptureCancelledError,
  CaptureUnsupportedError,
  canvasFromFile,
  captureScreen,
  encodeUnderLimit,
  hideOverlays,
  renderAnnotated,
} from '../../services/screenshot-capture';
import {
  ScreenshotEditorData,
  ScreenshotEditorDialogComponent,
} from '../screenshot-editor-dialog/screenshot-editor-dialog.component';

/**
 * The report dialog.
 *
 * Everything except the two fields the user fills in is gathered by `DiagnosticsService` when the
 * report is submitted. The user is never asked for their browser, their tenant or the page they
 * were on, because the app already knows all three and asking would be the fastest way to get a
 * report that says "it doesn't work".
 */
@Component({
  selector: 'app-report-bug-dialog',
  templateUrl: './report-bug-dialog.component.html',
  standalone: false,
})
export class ReportBugDialogComponent {
  private readonly fb = inject(FormBuilder);
  private readonly reports = inject(BugReportService);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  readonly dialogRef = inject(MatDialogRef<ReportBugDialogComponent>);

  readonly maxDescription = MAX_DESCRIPTION;
  readonly submitting = this.reports.submitting;

  readonly form = this.fb.group({
    kind: this.fb.nonNullable.control<BugReportKind>('bug', Validators.required),
    description: this.fb.nonNullable.control('', [Validators.required, Validators.maxLength(MAX_DESCRIPTION)]),
  });

  /** The untouched capture. Annotations are drawn onto a copy, so any of them can be undone. */
  readonly screenshot = signal<HTMLCanvasElement | undefined>(undefined);
  /** The capture with every annotation drawn in. Exactly what gets uploaded. */
  readonly preview = signal<string | undefined>(undefined);
  readonly capturing = signal(false);
  readonly annotations = signal<Annotation[]>([]);

  @ViewChild('fileinput') fileinput?: ElementRef<HTMLInputElement>;

  async capture(): Promise<void> {
    this.capturing.set(true);
    // The user is reporting the page, not this dialog. Hidden for the whole capture, which also
    // means they can see what they are about to share while the browser's picker is open.
    const restoreOverlays = hideOverlays();
    try {
      this.setScreenshot(await captureScreen());
    } catch (error) {
      // Cancelling the picker is a normal thing to do, so it is silent. Anything else is worth
      // telling the user about, because they are waiting for a picture that never arrived.
      if (error instanceof CaptureCancelledError) return;
      this.snackBar.open(
        error instanceof CaptureUnsupportedError
          ? 'This browser cannot capture the screen. You can attach an image instead.'
          : 'Could not capture the screen. You can attach an image instead.',
        'Dismiss',
        { duration: 5000 },
      );
    } finally {
      restoreOverlays();
      this.capturing.set(false);
    }
  }

  openFileInput(): void {
    this.fileinput?.nativeElement.click();
  }

  async handleFileInput(files: FileList | null): Promise<void> {
    const file = files?.item(0);
    if (!file) return;
    try {
      this.setScreenshot(await canvasFromFile(file));
    } catch {
      this.snackBar.open('That file could not be read as an image.', 'Dismiss', { duration: 5000 });
    }
  }

  removeScreenshot(): void {
    this.screenshot.set(undefined);
    this.preview.set(undefined);
    this.annotations.set([]);
  }

  /**
   * Opens the full-size editor.
   *
   * Annotating in this dialog meant drawing on an image squeezed into a 560px column, which is
   * unusable for pointing at anything small. The editor owns all of that; this dialog only holds
   * the resulting list and re-renders the thumbnail from it.
   */
  editScreenshot(): void {
    const canvas = this.screenshot();
    if (!canvas) return;

    const data: ScreenshotEditorData = { canvas, annotations: this.annotations() };
    this.dialog
      .open(ScreenshotEditorDialogComponent, {
        data,
        width: '95vw',
        maxWidth: '1400px',
        // 88vh, not 92: leaves room for the overlay's own margin on a short screen. The panel
        // class strips the surface padding, which is what was pushing the action bar off-screen.
        height: '88vh',
        panelClass: 'bug-report-editor-panel',
        autoFocus: false,
        // It holds work in progress, so a mis-click on the backdrop must not discard it.
        disableClose: true,
      })
      .afterClosed()
      .subscribe((annotations: Annotation[] | undefined) => {
        // undefined means Cancel, which must leave the existing annotations alone. An empty array
        // is a real result: the reporter cleared them.
        if (!annotations) return;
        this.annotations.set(annotations);
        this.refreshPreview();
      });
  }

  private refreshPreview(): void {
    const base = this.screenshot();
    if (!base) return;
    this.preview.set(renderAnnotated(base, this.annotations()).toDataURL('image/png'));
  }

  async submit(): Promise<void> {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.submitting()) return;

    const { kind, description } = this.form.getRawValue();

    let image: Blob | undefined;
    const canvas = this.screenshot();
    if (canvas) {
      try {
        // The same renderer the preview uses, so what leaves the browser is exactly what the
        // reporter saw. Annotations are burned into the pixels, so a redaction genuinely removes
        // the covered content rather than hiding it under a layer someone could peel off.
        image = await encodeUnderLimit(renderAnnotated(canvas, this.annotations()));
      } catch {
        this.snackBar.open('The screenshot could not be prepared, so the report will be sent without it.', 'Dismiss', {
          duration: 5000,
        });
      }
    }

    try {
      const result = await this.reports.submit(kind, description, image);
      this.snackBar.open(
        result.screenshotAttached || !image
          ? 'Thank you. Your report has been sent.'
          : 'Your report was sent, but the screenshot could not be attached.',
        'Dismiss',
        { duration: 5000 },
      );
      this.dialogRef.close(result.reportId);
    } catch {
      this.snackBar.open('Your report could not be sent. Please try again.', 'Dismiss', { duration: 5000 });
    }
  }

  private setScreenshot(canvas: HTMLCanvasElement): void {
    this.screenshot.set(canvas);
    this.annotations.set([]);
    this.preview.set(canvas.toDataURL('image/png'));
  }
}
